import React from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getCarpoolById, deleteCarpool } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  ArrowLeft,
  Car,
  MapPin,
  Clock,
  Repeat,
  Users,
  DollarSign,
  Phone,
  Edit,
  Trash2,
  Mail,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";

export default function ViewCarpoolPage() {
  useRequireSectionVisible("showService");
  const { itemId } = useParams<{ itemId: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(
    getCarpoolById,
    { itemId: itemId || "" },
    {
      enabled: !!itemId,
    },
  );

  const deleteCarpoolFn = useAction(deleteCarpool);

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this listing?")) {
      try {
        await deleteCarpoolFn({ itemId: itemId! });
        handleGoBack();
      } catch (err) {
        console.error("Error deleting carpool:", err);
      }
    }
  };

  const handleEdit = () => {
    if (selectedLocation?.locationCode) {
      navigate(
        `/locations/${selectedLocation.locationCode}/carpooling/${itemId}/edit`,
      );
    } else {
      navigate(`/carpooling/${itemId}/edit`);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/carpooling`);
    } else {
      navigate("/carpooling");
    }
  };

  if (isLoading)
    return (
      <DashboardLayout>
        <div>Loading...</div>
      </DashboardLayout>
    );
  if (error || !item)
    return (
      <DashboardLayout>
        <div>Error loading carpool listing</div>
      </DashboardLayout>
    );

  const metadata = (item.metadata as any) || {};
  const isDriver = metadata.role === "DRIVER";

  return (
    <DashboardLayout>
      <div className="flex h-full">
        <div className="flex-1 bg-gray-50 p-6 overflow-y-auto">
          <button
            onClick={handleGoBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Back to Carpools
          </button>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 bg-gray-50/[0.3]">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-xl ${isDriver
                        ? "bg-blue-100 text-blue-600"
                        : "bg-green-100 text-green-600"
                        }`}
                    >
                      {isDriver ? (
                        <Car className="w-8 h-8" />
                      ) : (
                        <Users className="w-8 h-8" />
                      )}
                    </div>
                    <div>
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-1 ${isDriver
                          ? "bg-blue-100 text-blue-800"
                          : "bg-green-100 text-green-800"
                          }`}
                      >
                        {isDriver ? "OFFERING RIDE" : "REQUESTING RIDE"}
                      </span>
                      <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                        {metadata.origin}{" "}
                        <span className="text-gray-400 mx-2">to</span>{" "}
                        {metadata.destination}
                      </h1>
                    </div>
                    <div className="flex items-center gap-4">
                      <SubscribeButton contentId={item.id} />
                      {user && user.id === item.authorId && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleEdit}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={handleDelete}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 space-y-8">
                {/* Key Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Clock className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 font-medium">
                        Time
                      </div>
                      <div className="text-gray-900 font-semibold">
                        {metadata.time}
                      </div>
                      {metadata.isRecurring ? (
                        <div className="flex items-center gap-1 text-sm text-blue-600 mt-1">
                          <Repeat className="w-3 h-3" />
                          <span>{metadata.daysOfWeek?.join(", ")}</span>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 mt-1">
                          {metadata.date
                            ? new Date(metadata.date).toLocaleDateString(
                              "en-US",
                              {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              },
                            )
                            : "No date"}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <DollarSign className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 font-medium">
                        Cost
                      </div>
                      <div className="text-gray-900 font-semibold">
                        {metadata.cost
                          ? `$${metadata.cost} / seat`
                          : "Free / Negotiable"}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Users className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 font-medium">
                        Seats Available
                      </div>
                      <div className="text-gray-900 font-semibold">
                        {metadata.seats} seats
                      </div>
                    </div>
                  </div>

                  {isDriver && metadata.vehicle && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <Car className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium">
                          Vehicle
                        </div>
                        <div className="text-gray-900 font-semibold">
                          {metadata.vehicle}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                {item.description && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      Details
                    </h3>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                      {item.description}
                    </p>
                  </div>
                )}

                {/* Contact */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Contact Information
                  </h3>
                  <div className="flex flex-col gap-3">
                    {metadata.phoneNumber && (
                      <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                        <Phone className="w-5 h-5 text-blue-600" />
                        <span className="text-blue-900 font-medium">
                          {metadata.phoneNumber}
                        </span>
                      </div>
                    )}
                    {metadata.email && (
                      <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <span className="text-blue-900 font-medium">
                          {metadata.email}
                        </span>
                      </div>
                    )}
                    {!metadata.phoneNumber &&
                      !metadata.email &&
                      metadata.contactInfo && (
                        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                          <Phone className="w-5 h-5 text-blue-600" />
                          <span className="text-blue-900 font-medium">
                            {metadata.contactInfo}
                          </span>
                        </div>
                      )}
                    {!metadata.phoneNumber &&
                      !metadata.email &&
                      !metadata.contactInfo && (
                        <div className="text-gray-500 italic">
                          No contact information provided
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
